<script setup lang="ts">
import MButton from "./buttons/MButton.vue";
import { IconArrowRight } from "@iconify-prerendered/vue-carbon";
import Secured from "../assets/icons/verification_secured.png";
import { getRouteOnboardPersonalProfile } from "@/views/Onboarding/OnboardingPersonalProfile.vue";
import { getRouteOnboardPersonalQuestionnaire } from "@/views/Onboarding/OnboardingPersonalQuestionnaire.vue";
import { getRouteOnboardPersonalVerification } from "@/views/Onboarding/OnboardingPersonalVerification.vue";
import { usePersonStore } from "@/store";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { RouteNames } from "@/utils/constants.ts";

const personStore = usePersonStore();
const { person, currentStepKey } = storeToRefs(personStore);

const isAuthenticated = computed(() => person.value !== null);
const personProfile = computed(() => person.value?.profile);

// Background color based on verification status
const bgColor = computed(() => {
  if (!personProfile.value) return "bg-rose-200";
  if (personProfile.value.status === "IN_REVIEW") return "bg-[#FFF9C5]";
  return "bg-[#FFE8AC]";
});

// Text based on verification status
const text = computed(() => {
  if (!isAuthenticated.value) return "Please verify your account to access all of Moneterum";
  if (!personProfile.value) return "Please complete your profile to continue";
  if (personProfile.value.status === "IN_REVIEW") return "Verification in Process";
  return "Verification Successfully Completed: 1 of 4";
});

// Button text based on authentication and profile status
const btnText = computed(() => {
  if (!isAuthenticated.value) return 'Start Verification';
  if (!personProfile.value) return 'Start Verification';
  if (personProfile.value.status === "IN_REVIEW") return 'Continue';
  return 'Continue';
});

// Button route - navigate to current step or first step by default
const btnRoute = computed(() => {
  // If there's a current step key, navigate to that step
  if (currentStepKey.value) {
    switch (currentStepKey.value) {
      case RouteNames.ONBOARDING_PERSON_PROFILE:
        return getRouteOnboardPersonalProfile();
      case RouteNames.ONBOARDING_PERSON_QUESTIONNAIRE:
        return getRouteOnboardPersonalQuestionnaire();
      case RouteNames.ONBOARDING_PERSON_PROFILE_VERIFY:
        return getRouteOnboardPersonalVerification();
      default:
        break;
    }
  }

  // Default to first step (Personal Profile)
  return getRouteOnboardPersonalProfile();
});
</script>

<template>
  <div
    class="min-h-[86px] w-full rounded-2xl shadow p-4 md:p-0"
    :class="bgColor"
  >
    <div
      class="w-full text-zinc-800 text-lg md:text-2xl font-bold font-['Sora'] leading-[38px] flex flex-col md:flex-row md:justify-between md:items-center items-center gap-4"
    >
      <div class="flex items-center gap-3 text-center md:text-left w-full md:w-auto">
        <img :src="Secured" alt="Secured" class="md:ml-[14px] h-[60px] md:h-[86px] flex-shrink-0" />
        <span>{{ text }}</span>
      </div>
      <div class="w-full md:w-auto md:mr-4">
        <MButton size2="medium" kind="primary" :to="btnRoute" class="w-full md:w-auto">
          {{ btnText }}
          <IconArrowRight />
        </MButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
